import { useEffect, useMemo, useState } from "react";
import PageHero from "../../components/PageHero.jsx";
import { fetchPublicStreamers } from "../../lib/publicDbApi.js";

const TWITCH_ICON = "https://racevision.info/assets/twitch.png";
const YOUTUBE_ICON = "https://racevision.info/assets/youtube.png";

const STATIC_STREAMERS = [
    {
        season_driver_id: "static-racevisiontv",
        driver_name: "offizieller Liga Stream",
        stream_url: "https://www.youtube.com/@RacevisionTV",
        team_name: "RaceVision",
        profile_image_url: "",
    },
    {
        season_driver_id: "static-nolimit",
        driver_name: "NoLimit Broadcast",
        stream_url: "https://www.youtube.com/@nolimitmotorsporttv",
        team_name: "NoLimit Motorsport",
        profile_image_url: "",
    },
    {
        season_driver_id: "static-kimivf",
        driver_name: "Patrick Valente Ferro",
        stream_url: "https://www.twitch.tv/kimivf",
        team_name: "Flying Arrow Racing",
        profile_image_url: "",
    },
];

function normalizeUrl(value) {
    const str = String(value ?? "").trim();
    if (!str) return "";
    if (/^https?:\/\//i.test(str)) return str;
    return `https://${str.replace(/^https?:\/\//i, "")}`;
}

function getPlatform(url) {
    const href = normalizeUrl(url).toLowerCase();
    if (href.includes("twitch.tv")) return "twitch";
    if (href.includes("youtube.com") || href.includes("youtu.be")) return "youtube";
    return "";
}

function platformLabel(platform) {
    if (platform === "twitch") return "Twitch";
    if (platform === "youtube") return "YouTube";
    return "Stream";
}

function platformIcon(platform) {
    if (platform === "twitch") return TWITCH_ICON;
    if (platform === "youtube") return YOUTUBE_ICON;
    return "";
}

function getInitials(name) {
    const parts = String(name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);

    if (!parts.length) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

    return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

function getProfileImage(entry) {
    return (
        entry?.profile_image_url ||
        entry?.avatar_url ||
        entry?.profile_image ||
        entry?.channel_avatar_url ||
        ""
    );
}

function StreamerRow({ entry }) {
    const href = normalizeUrl(entry?.stream_url);
    const platform = getPlatform(href);
    const iconSrc = platformIcon(platform);
    const profileImage = getProfileImage(entry);
    const driverName = entry?.driver_name || "Unbekannter Fahrer";
    const teamName = entry?.team_name || "Kein Team hinterlegt";

    return (
        <article className="streamer-list-row">
            <div className="streamer-list-main">
                <div className="streamer-avatar" aria-hidden="true">
                    {profileImage ? (
                        <img
                            src={profileImage}
                            alt=""
                            onError={(event) => {
                                event.currentTarget.style.display = "none";
                                const fallback = event.currentTarget.nextElementSibling;
                                if (fallback) fallback.style.display = "grid";
                            }}
                        />
                    ) : null}

                    <div
                        className="streamer-avatar-fallback"
                        style={{ display: profileImage ? "none" : "grid" }}
                    >
                        {getInitials(driverName)}
                    </div>
                </div>

                <div className="streamer-driver-info">
                    <strong>{driverName}</strong>
                    <span>{teamName}</span>
                </div>
            </div>

            <div className="streamer-platform">
                {iconSrc ? (
                    <img
                        src={iconSrc}
                        alt=""
                        aria-hidden="true"
                    />
                ) : null}
                <span>{platformLabel(platform)}</span>
            </div>

            <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className="button button--secondary streamer-open-link"
            >
                Kanal öffnen
            </a>
        </article>
    );
}

export default function Streamer() {
    const [rows, setRows] = useState([]);
    const [seasonName, setSeasonName] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;

        async function load() {
            try {
                setLoading(true);
                setError("");

                const res = await fetchPublicStreamers({
                    seriesKey: "gt3x",
                    seasonKey: "active",
                });

                if (cancelled) return;

                setRows(Array.isArray(res?.rows) ? res.rows : []);
                setSeasonName(res?.season?.display_name || "");
            } catch (e) {
                if (!cancelled) {
                    const message = String(e?.message || "");

                    // Backend endpoint noch nicht deployed:
                    // statische Streamer trotzdem anzeigen.
                    if (
                        message.toLowerCase().includes("not found") ||
                        message.includes("404")
                    ) {
                        setRows([]);
                        setSeasonName("");
                        setError("");
                    } else {
                        setError(
                            message || "Fehler beim Laden der Streamer-Daten."
                        );
                    }
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();

        return () => {
            cancelled = true;
        };
    }, []);

    const displayRows = useMemo(() => {
        // Backend-Zeilen zuerst: dort kommt das echte Twitch-/YouTube-Profilbild mit.
        // STATIC_STREAMERS bleiben nur als Fallback, falls der Backend-Endpunkt noch nicht aktualisiert ist.
        const merged = [...rows, ...STATIC_STREAMERS];
        const seen = new Set();

        return merged.filter((entry) => {
            const key = normalizeUrl(entry?.stream_url).toLowerCase();

            if (!key || seen.has(key)) return false;

            seen.add(key);
            return true;
        });
    }, [rows]);

    const stats = useMemo(() => {
        const twitch = displayRows.filter(
            (entry) => getPlatform(entry?.stream_url) === "twitch"
        ).length;

        const youtube = displayRows.filter(
            (entry) => getPlatform(entry?.stream_url) === "youtube"
        ).length;

        return {
            total: displayRows.length,
            twitch,
            youtube,
        };
    }, [displayRows]);

    return (
        <div className="rv-page streamer-page">
            <PageHero kicker="GT3 Xperience" title="Streamer">
                Hier findest du Stream-Links von aktiven Fahrern der aktuellen Saison.
                Perfekt, um Trainings, Rennen oder Onboards live mitzuverfolgen.
            </PageHero>

            <section className="rv-modern-card">
                <div className="rv-section-head">
                    <div>
                        <div className="rv-section-kicker">Broadcast</div>
                        <h2>Live Channels{seasonName ? ` / ${seasonName}` : ""}</h2>
                    </div>

                    <div className="rv-pill-row">
                        <span className="rv-status-pill rv-status-pill--info">
                            Gesamt: {stats.total}
                        </span>
                        <span className="rv-status-pill rv-status-pill--warn">
                            Twitch: {stats.twitch}
                        </span>
                        <span className="rv-status-pill rv-status-pill--ok">
                            YouTube: {stats.youtube}
                        </span>
                    </div>
                </div>

                {loading && (
                    <p className="rv-modern-muted">Lade Streamer-Liste ...</p>
                )}

                {!loading && error && (
                    <p style={{ color: "var(--err)" }}>{error}</p>
                )}

                {!loading && !error && displayRows.length === 0 && (
                    <div className="streamer-empty">
                        <h2>Noch keine Streamer eingetragen</h2>
                        <p className="rv-modern-muted">
                            Sobald aktive Saisonfahrer einen Stream-Link haben,
                            erscheinen sie hier.
                        </p>
                    </div>
                )}

                {!loading && !error && displayRows.length > 0 && (
                    <div className="streamer-list">
                        {displayRows.map((entry) => (
                            <StreamerRow
                                key={`${entry.season_driver_id}-${entry.stream_url}`}
                                entry={entry}
                            />
                        ))}
                    </div>
                )}
            </section>

            <style>
                {`
                    .streamer-list {
                        display: grid;
                        gap: 8px;
                        margin-top: 6px;
                    }

                    .streamer-list-row {
                        display: grid;
                        grid-template-columns: minmax(0, 1fr) auto auto;
                        gap: 16px;
                        align-items: center;
                        min-height: 70px;
                        padding: 10px 12px;
                        border-radius: 14px;
                        background: rgba(255,255,255,.028);
                        border: 1px solid rgba(255,255,255,.07);
                        transition:
                            background .16s ease,
                            border-color .16s ease,
                            transform .16s ease;
                    }

                    .streamer-list-row:hover {
                        background: rgba(255,255,255,.045);
                        border-color: rgba(228,254,60,.22);
                        transform: translateY(-1px);
                    }

                    .streamer-list-main {
                        min-width: 0;
                        display: flex;
                        align-items: center;
                        gap: 12px;
                    }

                    .streamer-avatar {
                        width: 48px;
                        height: 48px;
                        flex: 0 0 48px;
                        overflow: hidden;
                        border-radius: 999px;
                        background: rgba(255,255,255,.05);
                        border: 1px solid rgba(228,254,60,.22);
                    }

                    .streamer-avatar img,
                    .streamer-avatar-fallback {
                        width: 100%;
                        height: 100%;
                    }

                    .streamer-avatar img {
                        display: block;
                        object-fit: cover;
                    }

                    .streamer-avatar-fallback {
                        place-items: center;
                        color: var(--accent);
                        font-family: var(--ff-Kallisto), var(--ff-Manrope), sans-serif;
                        font-size: 15px;
                        font-weight: 900;
                        letter-spacing: .04em;
                        background:
                            radial-gradient(circle at 35% 25%, rgba(228,254,60,.14), transparent 45%),
                            rgba(255,255,255,.035);
                    }

                    .streamer-driver-info {
                        min-width: 0;
                        display: grid;
                        gap: 2px;
                    }

                    .streamer-driver-info strong {
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        font-size: 16px;
                        line-height: 1.25;
                    }

                    .streamer-driver-info span {
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        color: var(--muted);
                        font-family: var(--ff-Everett), sans-serif;
                        font-size: 13px;
                    }

                    .streamer-platform {
                        display: inline-flex;
                        align-items: center;
                        gap: 7px;
                        min-width: 92px;
                        color: var(--muted);
                        font-size: 13px;
                        font-weight: 800;
                    }

                    .streamer-platform img {
                        width: 20px;
                        height: 20px;
                        object-fit: contain;
                    }

                    .streamer-open-link {
                        padding: 8px 12px;
                        border-radius: 10px;
                        white-space: nowrap;
                        text-decoration: none;
                        box-shadow: none;
                    }

                    .streamer-empty {
                        text-align: center;
                        padding: 28px;
                        border-radius: 18px;
                        background: rgba(255,255,255,.035);
                        border: 1px solid rgba(255,255,255,.08);
                    }

                    @media (max-width: 720px) {
                        .streamer-list-row {
                            grid-template-columns: minmax(0, 1fr) auto;
                            gap: 10px 12px;
                        }

                        .streamer-platform {
                            grid-column: 1;
                            margin-left: 60px;
                            min-width: 0;
                        }

                        .streamer-open-link {
                            grid-column: 2;
                            grid-row: 1 / span 2;
                            align-self: center;
                        }
                    }

                    @media (max-width: 520px) {
                        .streamer-list-row {
                            grid-template-columns: 1fr;
                            align-items: stretch;
                        }

                        .streamer-platform {
                            grid-column: 1;
                            grid-row: auto;
                            margin-left: 60px;
                        }

                        .streamer-open-link {
                            grid-column: 1;
                            grid-row: auto;
                            margin-left: 60px;
                            width: fit-content;
                        }
                    }
                `}
            </style>
        </div>
    );
}
